
import { reportError } from "@/utils/monitoring";

// Define types for performance metrics
export interface NavigationMetrics {
  routeName: string;
  startTime: number;
  loadTime?: number;
  renderTime?: number;
  totalTime?: number;
}

// Store for active navigation
let activeNavigation: NavigationMetrics | null = null;
let navigationHistory: NavigationMetrics[] = [];

/**
 * Start tracking a page navigation
 * @param routeName The route being navigated to
 */
export function startPageNavigation(routeName: string): void {
  try {
    // If there's already an active navigation, complete it before starting a new one
    if (activeNavigation) {
      console.warn('Previous navigation was not completed', activeNavigation);
      completePageNavigation();
    }

    // Record the start of navigation
    activeNavigation = {
      routeName,
      startTime: performance.now(),
    };
    
    console.log(`📊 Navigation started to ${routeName}`);
  } catch (error) {
    reportError(error);
  }
}

/**
 * Record when the page load completes
 */
export function completePageNavigation(): void {
  try {
    if (!activeNavigation) {
      console.warn('No active navigation to complete');
      return;
    }

    const endTime = performance.now();
    const totalTime = endTime - activeNavigation.startTime;

    // Complete the metrics
    activeNavigation.totalTime = totalTime;
    
    // Add to history and log
    navigationHistory.push(activeNavigation);
    
    // Print to console in development
    console.log(`📊 Navigation completed: ${activeNavigation.routeName} (${totalTime.toFixed(2)}ms)`);
    
    if (totalTime > 1000) {
      console.warn(`⚠️ Slow navigation detected: ${activeNavigation.routeName} took ${totalTime.toFixed(2)}ms`);
    }
    
    // Reset active navigation
    activeNavigation = null;
  } catch (error) {
    reportError(error);
  }
}

/**
 * Mark when initial content render is complete
 */
export function markContentRendered(): void {
  try {
    if (!activeNavigation) return;
    
    const currentTime = performance.now();
    activeNavigation.renderTime = currentTime - activeNavigation.startTime;
    
    console.log(`📊 Content rendered: ${activeNavigation.renderTime.toFixed(2)}ms`);
  } catch (error) {
    reportError(error);
  }
}

/**
 * Get performance metrics history
 */
export function getNavigationHistory(): NavigationMetrics[] {
  return [...navigationHistory];
}

/**
 * Clear navigation history
 */
export function clearNavigationHistory(): void {
  navigationHistory = [];
}

/**
 * Analyze navigation metrics to identify slowest routes
 */
export function analyzeNavigationPerformance(): {
  slowestRoutes: Array<{ route: string, avgTime: number, count: number }>;
  averageLoadTime: number;
} {
  if (navigationHistory.length === 0) {
    return { slowestRoutes: [], averageLoadTime: 0 };
  }

  // Group by route
  const routeMetrics: Record<string, number[]> = {};
  
  navigationHistory.forEach(metrics => {
    if (!metrics.totalTime) return;
    
    if (!routeMetrics[metrics.routeName]) {
      routeMetrics[metrics.routeName] = [];
    }
    
    routeMetrics[metrics.routeName].push(metrics.totalTime);
  });
  
  // Calculate averages
  const routeAverages = Object.entries(routeMetrics).map(([route, times]) => {
    const total = times.reduce((sum, time) => sum + time, 0);
    return {
      route,
      avgTime: total / times.length,
      count: times.length
    };
  });
  
  // Sort by average time (descending)
  const slowestRoutes = routeAverages.sort((a, b) => b.avgTime - a.avgTime);
  
  // Calculate overall average
  const allTimes = navigationHistory
    .map(metrics => metrics.totalTime)
    .filter((time): time is number => time !== undefined);
  
  const averageLoadTime = allTimes.reduce((sum, time) => sum + time, 0) / allTimes.length;
  
  return {
    slowestRoutes: slowestRoutes.slice(0, 5), // Top 5 slowest
    averageLoadTime
  };
}
